/* eslint-disable import/order */
import React, { useContext, useRef, useState } from 'react';
import * as R from 'ramda';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { useParams } from 'react-router-dom';

// Helpers
import newSupplierAuthSteps from 'feature/loading-management/supplier-authorizations/create-new-steps/Steps';

// Context
import { DrawerContext } from 'common/drawer/DrawerContext';

// Utils
import { pluralize } from 'feature/loading-management/utils/format';
import { capitalize } from 'utils/helpers/format';
import { DRAWER_TYPES } from 'utils/constants';
import { ACTIVE_TAB_SELF_AUTH_MAP } from '../../../utils/const';
import { resetSupplierAuthWizardData } from '../../store/createNewSupplierAuthSlice';

// Components
import { Button, Menu, MenuItem, Popover, Typography } from '@mui/material';
import Wizard from 'common/Wizard';
import FilterPopover from '../../filter-popover/FilterPopover.connector';

// Icons
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import { filterCounterStyles } from '../CardsPanel.styles';
import { useUserRolePermissions } from 'hooks';

const marginRightBtNsStyles = { marginRight: theme => theme.spacing(3) };
const marginLeftBtNsStyles = { marginLeft: theme => theme.spacing(3) };

export const handleMenuButtonClick = (
  activeSupplierAuthTab,
  supplierAuthorizationsCardsLength,
  filterIdentifierCount,
  type,
  nameShort,
  setDialogDetails,
  supplierAuthorizationsCards,
  toggleBatchActivation,
  viewBy
) => {
  const toggleType = type === 'activate' ? 'activate' : 'deactivate';
  const productType = capitalize(ACTIVE_TAB_SELF_AUTH_MAP[activeSupplierAuthTab]);
  const dialogString = name => {
    const toggleLabel = toggleType === 'activate' ? 'On' : 'Off';
    const reverseToggleLabel = toggleType === 'activate' ? 'turned off' : 'turned on';

    if (!name) return `Turn ${toggleLabel} All Supplier Authorizations?`;
    return `Turn ${R.toLower(toggleLabel)} product loading for the ${pluralize(
      supplierAuthorizationsCardsLength,
      'Authorization'
    )}? Supplier Authorizations can be ${reverseToggleLabel} at any time.`;
  };

  const getSuccessMessage = (count, param) => {
    const toggleLabel = param === 'activate' ? 'turned on' : 'turned off';

    return `Product loading has been ${toggleLabel} for the ${pluralize(count, 'Authorization')}.`;
  };

  setDialogDetails({
    confirmAction: toggleBatchActivation,
    header: dialogString(),
    message: dialogString(nameShort),
    params: {
      filterIdentifierCount,
      productType,
      successMessage: getSuccessMessage(supplierAuthorizationsCards.length, toggleType),
      supplierAuthorizationsCards,
      toggleType,
      viewBy,
    },
  });
};

const HeaderButtons = ({
  activeSupplierAuthTab,
  filterIdentifierCount,
  nameShort,
  setDialogDetails,
  supplierAuthorizationsCards,
  supplierAuthorizationsPending,
  toggleBatchActivation,
}) => {
  const { viewBy } = useParams();
  const [turnOnOffMenuOpen, setTurnOnOffMenuOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const { setDrawerComponentProps } = useContext(DrawerContext);
  const { isProductLoadingManage } = useUserRolePermissions();
  const menuAnchorRef = useRef(null);

  const handleClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterClick = event => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleClick = type => {
    const toggleType = type !== 'activate';
    const supplierAuthorizationsCardsLength = supplierAuthorizationsCards?.filter(
      ({ active }) => active === toggleType
    ).length;
    handleMenuButtonClick(
      activeSupplierAuthTab,
      supplierAuthorizationsCardsLength,
      filterIdentifierCount,
      type,
      nameShort,
      setDialogDetails,
      supplierAuthorizationsCards,
      toggleBatchActivation,
      viewBy
    );
  };

  const handleNewAuthClick = () => {
    setDrawerComponentProps({
      Component: props => (
        <Wizard
          stepHeaderSx={{ pb: 1 }}
          steps={newSupplierAuthSteps}
          title="New Supplier Authorizations"
          {...props}
        />
      ),
      forceCloseSelector: state => state.loadingManagement.createNewSupplierAuth.forceClose,
      holdOpenSelector: state => state.loadingManagement.createNewSupplierAuth.submittingBatch,
      resetWizard: resetSupplierAuthWizardData,
      type: DRAWER_TYPES.createNewSupplierAuth,
    });
  };

  const handleMenuOpen = () => {
    setTurnOnOffMenuOpen(!turnOnOffMenuOpen);
  };

  const handleOnButtonOnClick = () => {
    handleClick('activate');
    handleMenuOpen();
  };
  const handleOffButtonOnClick = () => {
    handleClick('deactivate');
    handleMenuOpen();
  };

  return (
    <>
      <Button
        disabled={supplierAuthorizationsPending}
        onClick={handleFilterClick}
        onClose={handleClose}
        sx={marginRightBtNsStyles}
        variant="text"
      >
        Filters
        {filterIdentifierCount >= 1 && (
          <Typography sx={filterCounterStyles}>{filterIdentifierCount}</Typography>
        )}
        {filterAnchorEl ? (
          <ExpandLessRoundedIcon position="right" variant="buttonsChild" />
        ) : (
          <ExpandMoreRoundedIcon position="right" variant="buttonsChild" />
        )}
      </Button>
      <Popover
        anchorEl={filterAnchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        disableScrollLock
        onClose={handleClose}
        open={!!filterAnchorEl}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
      >
        <FilterPopover viewBy={viewBy} />
      </Popover>
      {isProductLoadingManage && (
        <>
          <Button
            disabled={!supplierAuthorizationsCards.length}
            onClick={handleMenuOpen}
            ref={menuAnchorRef}
            variant="outlined"
          >
            Turn On/Off
            {turnOnOffMenuOpen ? (
              <ExpandLessRoundedIcon position="right" variant="buttonsChild" />
            ) : (
              <ExpandMoreRoundedIcon position="right" variant="buttonsChild" />
            )}
          </Button>
          <Menu anchorEl={menuAnchorRef?.current} onClose={handleMenuOpen} open={turnOnOffMenuOpen}>
            <MenuItem
              disabled={R.all(R.propEq('active', true), supplierAuthorizationsCards)}
              onClick={handleOnButtonOnClick}
            >
              {`Turn ${
                supplierAuthorizationsCards && filterIdentifierCount
                  ? `${supplierAuthorizationsCards.length} On`
                  : 'On All'
              }`}
            </MenuItem>
            <MenuItem
              disabled={R.all(R.propEq('active', false), supplierAuthorizationsCards)}
              onClick={handleOffButtonOnClick}
            >
              {`Turn ${
                supplierAuthorizationsCards && filterIdentifierCount
                  ? `${supplierAuthorizationsCards.length} Off`
                  : 'Off All'
              }`}
            </MenuItem>
          </Menu>
          <Button onClick={handleNewAuthClick} sx={marginLeftBtNsStyles} variant="contained">
            New Authorizations
          </Button>
        </>
      )}
    </>
  );
};

export default HeaderButtons;

HeaderButtons.propTypes = {
  activeSupplierAuthTab: string,
  filterIdentifierCount: number.isRequired,
  nameShort: string,
  setDialogDetails: func.isRequired,
  supplierAuthorizationsCards: arrayOf(shape({})).isRequired,
  supplierAuthorizationsPending: bool.isRequired,
  toggleBatchActivation: func.isRequired,
};

HeaderButtons.defaultProps = {
  activeSupplierAuthTab: '',
  nameShort: '',
};
